// src/components/map/utils/rigFracIconSizeExpressions.ts
//
// Pure helper for the Mapbox GL icon-size expression shared by the Rigs and
// Frac Crews layers. The base zoom curve keeps markers small at national zoom
// and prominent at basin/pad zoom. A user size multiplier scales every stop,
// and the currently hovered feature is enlarged on top of that — both inputs
// compose in one expression so hover enlargement and user sizing never cancel
// each other out.
import { buildInterpolatedIconSizeExpression } from "./facilityIconSizeExpressions";

/**
 * Zoom stops for rig/frac icon sizing. Each entry is [zoom, baseSize] — the
 * baseSize is scaled by the user size multiplier (and, for the hovered feature,
 * the hover multiplier) at build time.
 */
const RIG_FRAC_ZOOM_STOPS: ReadonlyArray<readonly [number, number]> = [
  [2, 0.25],
  [4, 0.4],
  [10, 1.0],
  [12, 1.4],
  [22, 1.8]
];

/** How much larger the hovered feature renders over its multiplier-scaled base. */
const HOVER_SIZE_MULTIPLIER = 1.5;

export interface RigFracIconSizeOptions {
  /** User-configured icon size multiplier (1 leaves the base curve unchanged). */
  sizeMultiplier?: number;
  /** `id` of the feature to enlarge on hover, or null when nothing is hovered. */
  hoveredId?: Maybe<string>;
}

/**
 * Build a Mapbox `interpolate` expression for rig/frac icon sizing that scales
 * with zoom level, the user size multiplier, and — for the hovered feature —
 * the hover enlargement.
 *
 * With nothing hovered the result is the plain multiplier-scaled zoom curve
 * (no per-feature `case`), which is byte-identical to the layer-init expression
 * built in getMapLayers when the multiplier is 1.
 */
export function buildRigFracIconSizeExpression({
  sizeMultiplier = 1,
  hoveredId = null
}: RigFracIconSizeOptions = {}): unknown[] {
  return buildZoomCurve(RIG_FRAC_ZOOM_STOPS, sizeMultiplier, hoveredId);
}

function buildZoomCurve(
  zoomStops: ReadonlyArray<readonly [number, number]>,
  sizeMultiplier: number,
  hoveredId: Maybe<string>
): unknown[] {
  // Nothing hovered: return the plain zoom-interpolated base curve so the
  // resting expression matches layer init exactly.
  if (!hoveredId) {
    return buildInterpolatedIconSizeExpression(zoomStops, sizeMultiplier);
  }

  const stops: unknown[] = [];
  for (const [zoom, baseSize] of zoomStops) {
    const base = baseSize * sizeMultiplier;
    stops.push(zoom, [
      "case",
      // Mapbox's `==` is type-strict (no number/string coercion) — the tile's
      // `id` property isn't guaranteed to be a string, so cast it explicitly
      // before comparing against the (always-string) hovered id.
      ["==", ["to-string", ["get", "id"]], hoveredId],
      base * HOVER_SIZE_MULTIPLIER,
      base
    ]);
  }

  return ["interpolate", ["linear"], ["zoom"], ...stops];
}

/**
 * Outline width in screen px per unit of icon-size: the Insights map icon's
 * stroke at the glyph scale of rig-outlined.png / frac-crew-outlined.png
 * (pixelRatio 4). Those PNGs are generated distance fields, not plain images;
 * regenerate them and these constants together with scripts/rigfrac-sdf/gen.py.
 */
export const RIG_OUTLINE_PX_PER_ICON_SIZE = 1.26;
export const FRAC_OUTLINE_PX_PER_ICON_SIZE = 1.08;

/**
 * `icon-halo-width` matching the icon-size built with the same options, so the
 * outline scales with the icon like Insights' baked-in stroke.
 */
export function buildRigFracHaloWidthExpression(
  outlinePxPerIconSize: number,
  { sizeMultiplier = 1, hoveredId = null }: RigFracIconSizeOptions = {}
): unknown[] {
  // No outline at the lowest stop: the halo is not shrunk by perspective, and on far
  // markers in a pitched view it would tint the icon's whole padding box.
  const outlineStops = RIG_FRAC_ZOOM_STOPS.map(
    ([zoom, baseSize], i) =>
      [zoom, i === 0 ? 0 : baseSize * outlinePxPerIconSize] as const
  );
  return buildZoomCurve(outlineStops, sizeMultiplier, hoveredId);
}
